import type {
  ResearchDrawerCase,
  ResearchDrawerSource,
  ResearchInformationDetail,
} from './research-information';
import { modalDismissProps, useModalFocus } from './scenario-modal-focus';

/**
 * “需要掌握的信息”里面“查看依据”打开的侧边说明。
 *
 * 与高层建筑火灾的依据抽屉同一个形状：读者点一次就能看到这一项为什么需要、
 * 需要拿到什么、缺失时会怎样、它能说明和不能说明什么，以及相关的官方资料与案例。
 * 不再把资料折叠在信息行下面，让读者一层层展开。
 *
 * 抽屉只解释“需要了解什么”，不表示已经取得，也不给出阈值、距离或处置动作。
 */
export function ResearchInformationDrawer({
  sceneTitle,
  issueTitle,
  needName,
  acquisition,
  detail,
  sources,
  cases,
  restoreFocusTo,
  onClose,
}: {
  readonly sceneTitle: string;
  readonly issueTitle: string;
  readonly needName: string;
  readonly acquisition: string;
  readonly detail: ResearchInformationDetail;
  readonly sources: readonly ResearchDrawerSource[];
  readonly cases: readonly ResearchDrawerCase[];
  readonly restoreFocusTo?: HTMLElement | null;
  readonly onClose: () => void;
}) {
  const dialog = useModalFocus(restoreFocusTo);
  return (
    <dialog
      className="scc-drawer scc-information-drawer"
      ref={dialog}
      role="dialog"
      aria-modal="true"
      aria-labelledby="scc-information-title"
      {...modalDismissProps(onClose)}
    >
      <div className="scc-drawer-heading">
        <div>
          <p className="scc-drawer-eyebrow">
            {sceneTitle} · {issueTitle}
          </p>
          <h2 id="scc-information-title">{needName}</h2>
        </div>
        <button autoFocus onClick={onClose} aria-label="关闭">
          关闭 ×
        </button>
      </div>
      <div className="scc-drawer-body">
        <p className="scc-notice" data-information-nature="NOT_YET_OBTAINED">
          这一项说明的是需要了解什么，未表示当前已经取得；缺失的部分按“待核实”保留。
        </p>
        <section className="scc-drawer-section">
          <h3>为什么需要这项信息</h3>
          <p>{detail.why}</p>
        </section>
        <section className="scc-drawer-section">
          <h3>需要获取什么</h3>
          <ul className="scc-drawer-list">
            {detail.what.map((row) => (
              <li key={row}>{row}</li>
            ))}
          </ul>
        </section>
        <section className="scc-drawer-section">
          <h3>通常从哪里获得</h3>
          <p>{acquisition}</p>
        </section>
        <section className="scc-drawer-section">
          <h3>缺失或不可靠时会怎样</h3>
          <p>{detail.risk}</p>
        </section>
        <section className="scc-drawer-section">
          <h3>能说明什么、不能说明什么</h3>
          <p>{detail.boundary}</p>
        </section>
        <section className="scc-drawer-section">
          <h3>相关官方资料</h3>
          {sources.length === 0 ? (
            <p className="scc-drawer-note">
              本轮未登记与该项直接对应的官方资料，不借用其他资料填充。
            </p>
          ) : (
            <div className="scc-drawer-sources">
              {sources.map((source) => (
                <article
                  className="scc-evidence-card"
                  data-drawer-source={source.id}
                  key={source.id}
                >
                  <span className="scc-chip">{source.meta}</span>
                  <h4>{source.title}</h4>
                  <p>{source.summary}</p>
                  <p className="scc-evidence-use">与本项的关系：{source.relation}</p>
                  {source.caveat && <p className="scc-evidence-classification">{source.caveat}</p>}
                  <a href={source.url} target="_blank" rel="noopener noreferrer">
                    阅读官方原文 ↗
                  </a>
                  {source.documentUrl && source.documentLabel && (
                    <a href={source.documentUrl} target="_blank" rel="noopener noreferrer">
                      {source.documentLabel} ↗
                    </a>
                  )}
                </article>
              ))}
            </div>
          )}
        </section>
        {cases.length > 0 && (
          <section className="scc-drawer-section">
            <h3>本问题的相关案例</h3>
            <div className="scc-drawer-sources">
              {cases.map((row) => (
                <article className="scc-evidence-card" data-drawer-case={row.id} key={row.id}>
                  <span className="scc-chip">{row.meta}</span>
                  <h4>{row.title}</h4>
                  <p>{row.summary}</p>
                  {row.caveat && <p className="scc-evidence-classification">{row.caveat}</p>}
                  {row.url ? (
                    <a href={row.url} target="_blank" rel="noopener noreferrer">
                      查看官方案例原文 ↗
                    </a>
                  ) : (
                    <p className="scc-chem-unverified-link">原文链接待核实，暂不提供外链。</p>
                  )}
                </article>
              ))}
            </div>
          </section>
        )}
        <p className="scc-drawer-note">
          官方原文外链；目标网络与 PDF 可达性未验证。案例记录的是历史事件或演练，
          不代表当前现场情况。
        </p>
      </div>
    </dialog>
  );
}
